import React, { useState } from 'react';
import { bool, func, string } from 'prop-types';
import classNames from 'classnames';
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';

import { PrimaryButton } from '../../components';

import css from './StripePaymentElementForm.module.css';

/**
 * Single reusable payment form used by all 5 embedded payment flows.
 *
 * Must be rendered inside a Stripe <Elements> provider that was initialised
 * with the `clientSecret` for this payment (see StripePaymentModal). The form
 * itself only reads the Stripe/Elements instances from context — the
 * `clientSecret` prop is used purely as a readiness guard so callers can mount
 * the form before the secret has been fetched.
 *
 * On submit it calls stripe.confirmPayment with redirect: 'if_required', which
 * keeps the user on Wheelplace for card payments and only redirects when a
 * payment method genuinely requires it (3DS fallback → returnUrl). No
 * cardholder data ever touches our servers (BR-07).
 */
const StripePaymentElementForm = props => {
  const {
    rootClassName,
    className,
    clientSecret,
    returnUrl,
    onSuccess,
    onError,
    onPending,
    onProcessingChange,
    submitLabel,
    isLoading,
  } = props;

  const stripe = useStripe();
  const elements = useElements();

  const [errorMessage, setErrorMessage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const setProcessing = next => {
    setIsProcessing(next);
    if (onProcessingChange) {
      onProcessingChange(next);
    }
  };

  // Stripe.js hasn't loaded, the Elements provider has no clientSecret yet, or
  // the caller is still preparing the payment. Render the shimmer placeholder.
  const isReady = !!stripe && !!elements && !!clientSecret && !isLoading;

  const handleSubmit = async event => {
    event.preventDefault();

    if (!stripe || !elements || isProcessing) {
      return;
    }

    setProcessing(true);
    setErrorMessage(null);

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: returnUrl ? { return_url: returnUrl } : {},
      // Stay in-page whenever possible; only redirect when the payment method
      // (e.g. a 3DS challenge that needs a full redirect) requires it.
      redirect: 'if_required',
    });

    if (error) {
      // Card errors and validation errors are shown inline; the user stays on
      // the same page (BR-05).
      const message = error.message || 'Something went wrong with your payment.';
      setErrorMessage(message);
      setProcessing(false);
      if (onError) {
        onError(error);
      }
      return;
    }

    // No error and no redirect means the PaymentIntent resolved in-page. For
    // manual-capture flows the status is 'requires_capture'; for immediate
    // capture / subscriptions it is 'succeeded'. Both are a success here. An
    // async method that resolves in-page to 'processing' is NOT yet confirmed —
    // surface it as pending so the booking isn't marked paid prematurely.
    setProcessing(false);
    if (paymentIntent?.status === 'processing') {
      if (onPending) {
        onPending(paymentIntent);
      }
      return;
    }
    if (onSuccess) {
      onSuccess(paymentIntent);
    }
  };

  const classes = classNames(rootClassName || css.root, className);

  if (!isReady) {
    return (
      <div className={classes}>
        <div className={css.shimmer} aria-hidden="true">
          <div className={css.shimmerLine} />
          <div className={css.shimmerLine} />
          <div className={css.shimmerLineShort} />
        </div>
      </div>
    );
  }

  return (
    <form className={classes} onSubmit={handleSubmit}>
      <PaymentElement
        options={{ layout: 'tabs' }}
      />

      {errorMessage ? (
        <p className={css.error} role="alert">
          {errorMessage}
        </p>
      ) : null}

      <PrimaryButton
        type="submit"
        className={css.submitButton}
        inProgress={isProcessing}
        disabled={isProcessing}
        aria-busy={isProcessing}
      >
        {submitLabel}
      </PrimaryButton>
    </form>
  );
};

StripePaymentElementForm.defaultProps = {
  rootClassName: null,
  className: null,
  returnUrl: null,
  onSuccess: null,
  onError: null,
  onPending: null,
  onProcessingChange: null,
  submitLabel: 'Pay',
  isLoading: false,
};

StripePaymentElementForm.propTypes = {
  rootClassName: string,
  className: string,
  clientSecret: string.isRequired,
  returnUrl: string,
  onSuccess: func,
  onError: func,
  onPending: func,
  onProcessingChange: func,
  submitLabel: string,
  isLoading: bool,
};

export default StripePaymentElementForm;
